import { useCallback, useEffect, useRef, useState } from 'react'

interface UseVisibleReportIdsOptions {
  reportIds: readonly string[]
  actorId?: string
  organizationId?: string
  enabled: boolean
}

/** Owns only viewport observation; result scheduling remains in its caller. */
export const useVisibleReportIds = ({
  reportIds,
  actorId,
  organizationId,
  enabled,
}: UseVisibleReportIdsOptions) => {
  const [visibleIds, setVisibleIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const nodesRef = useRef(new Map<string, HTMLElement>())
  const observerRef = useRef<IntersectionObserver | null>(null)

  const markVisible = useCallback((reportId: string) => {
    if (!reportId) return
    setVisibleIds((current) =>
      current.has(reportId) ? current : new Set([...current, reportId]),
    )
  }, [])

  const register = useCallback(
    (reportId: string) =>
      (node: HTMLElement | null): void => {
        const previous = nodesRef.current.get(reportId)
        if (!node) {
          if (previous) observerRef.current?.unobserve(previous)
          nodesRef.current.delete(reportId)
          return
        }
        if (previous && previous !== node)
          observerRef.current?.unobserve(previous)
        nodesRef.current.set(reportId, node)
        if (typeof IntersectionObserver === 'undefined') {
          markVisible(reportId)
          return
        }
        if (!observerRef.current) {
          observerRef.current = new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                if (entry.isIntersecting || entry.intersectionRatio > 0) {
                  markVisible(
                    (entry.target as HTMLElement).dataset.liveReportId ?? '',
                  )
                }
              }
            },
            { rootMargin: '320px 0px', threshold: 0.01 },
          )
        }
        node.dataset.liveReportId = reportId
        observerRef.current.observe(node)
      },
    [markVisible],
  )

  useEffect(() => {
    observerRef.current?.disconnect()
    observerRef.current = null
    nodesRef.current.clear()
    setVisibleIds(new Set())
  }, [actorId, enabled, organizationId])

  useEffect(() => {
    const currentIds = new Set(reportIds)
    setVisibleIds((current) => {
      const next = new Set([...current].filter((id) => currentIds.has(id)))
      return next.size === current.size ? current : next
    })
  }, [reportIds])

  useEffect(
    () => () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    },
    [],
  )

  return { register, visibleIds }
}
